// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import Flag from 'app/common/react/components/undiscovery/Flag/Flag';
import i18n from 'app/common/lib/i18n';
// eslint-disable-next-line pinterest/no-deprecated-imports
import LegacyButton from 'app/common/react/ui/deprecated/LegacyButton';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import templateConst from 'app/common/lib/templateConst';
import type { CommentUser } from 'app/packages/pinterest-flow-types/user';
import urlUtil from 'app/common/lib/urlUtil';
import { reportUserDidItData } from 'app/common/redux/actions/user_did_it_data/did_it';
import { type Dispatch } from 'redux';
import { Box, Text } from 'gestalt';
import { Flow, Status, FlagType } from 'app/common/react/components/undiscovery/UndiscoveryEnums';

const ABOUT_DMCA_PIN_URL = templateConst.settings.ABOUT_DMCA_PIN_URL;
const IGNORE_THIS_PIN_CASE = 'ignoreThisPin';
const IP_CASE = 'IP';
const POLICIES_CASE = 'policies';
const SPAM_CASE = 'spam';
const ATTACKS_CASE = 'attacks';

type ReduxDispatchProps = {|
  reportDidIt: (
    aggregatedPinDataId: string,
    detailedReasons: ?Array<?string>,
    id: string,
    reason: ?string,
    username: string,
  ) => void,
|};

type OwnProps = {|
  aggregatedPinDataId: string,
  didItDataId: string,
  pinId: string,
  username: string,

  // pass-thru props to Flag
  onClose: (boolean) => void,
  users?: Array<CommentUser>,
|};

type Props = {|
  ...ReduxDispatchProps,
  ...OwnProps,
|};

type State = {|
  category: ?string,
  detailedReasons: Array<?string>,
  flagReason: ?string,
  flow: string,
  selectedReason: ?string,
  status: string,
|};

export class FlagDidItActivity extends Component<Props, State> {
  state = {
    flow: Flow.REPORT,
    status: Status.STARTED,

    // populated when the user selects a reason
    selectedReason: undefined,

    // push selectedReason into detailedReasons as user progresses thru flow
    // pop the value as the user moves back
    detailedReasons: [],

    // populated when the user submits selectedReason or proceeds to the next step
    flagReason: undefined,

    // associated with the terminal reason; is submitted as options.
    category: undefined,
  };

  getTitle = () => {
    const { flow, flagReason, detailedReasons } = this.state;

    if (flow === Flow.REPORT) {
      // Select a title based on the flag reason
      switch (flagReason) {
        case ATTACKS_CASE:
          return i18n._("What's going on?", 'title for report content flow');
        case SPAM_CASE:
        case POLICIES_CASE:
          return i18n._("What's wrong with this Try?", 'title for report content flow');
        case IP_CASE:
          return i18n._('Intellectual Property', 'title for report content flow for IP complaints');
        default:
          return i18n._("What's wrong with this Try?", 'title for the report content flow');
      }
    } else if (flow === Flow.END) {
      if (detailedReasons.includes('notUseful')) {
        return i18n._(
          'Thanks for letting us know!',
          'title at end of report flow when content is not useful',
        );
      }
    }

    return '';
  };

  // User selects a report reason
  handleSelectReason = (checked: boolean, value: string, category: string) => {
    if (checked) {
      this.setState({
        selectedReason: value,
        status: Status.SELECTED,
        category,
      });
    }
  };

  handleNext = () => {
    // When the user clicks 'next', push the selectedReason into detailedReasons,
    // and make the selectedReason the current flagReason
    this.setState({
      flagReason: this.state.selectedReason,
      detailedReasons: this.state.detailedReasons.concat([this.state.selectedReason] || []),
      selectedReason: undefined,
    });
  };

  handleRevert = () => {
    // clone and pop
    const detailedReasons = this.state.detailedReasons.slice(0, -1);

    // set the flagReason (which determines which set of flagOptions to show)
    // to the lastDetailedReason
    const lastDetailedReason = detailedReasons[detailedReasons.length - 1];

    this.setState({
      selectedReason: undefined,
      flagReason: lastDetailedReason,
      detailedReasons,
      category: undefined,
      status: typeof lastDetailedReason === 'undefined' ? Status.STARTED : Status.SELECTED,
    });
  };

  handleSubmit = () => {
    const { category, detailedReasons, selectedReason } = this.state;
    this.setState({
      status: Status.PROCESSING,
    });

    if (selectedReason === IGNORE_THIS_PIN_CASE) {
      this.setState({
        // Go to the Block & Unfollow flow
        flow: Flow.BLOCK,
        status: Status.STARTED,
      });
    } else {
      // Add the latest reason to detailedReasons
      const newDetailedReasons = detailedReasons.concat(selectedReason);

      this.setState({
        detailedReasons: newDetailedReasons,
      });

      const { aggregatedPinDataId, didItDataId, reportDidIt, username } = this.props;

      reportDidIt(aggregatedPinDataId, newDetailedReasons, didItDataId, category, username);
      if (detailedReasons.includes('notUseful')) {
        this.setState({
          flow: Flow.END,
          status: Status.STARTED,
        });
      } else {
        this.setState({
          flow: Flow.BLOCK,
          status: Status.STARTED,
        });
      }
    }
  };

  handleFlowStatusChange = (flowStatus: State, cb: any) => {
    this.setState(flowStatus, cb);
  };

  // Define the edge cases that override the default renderContent behavior defined in Flag.js
  renderContent = () => {
    const { flagReason } = this.state;

    const { didItDataId, pinId } = this.props;
    if (flagReason === 'IP') {
      return (
        <Box md={{ display: 'flex' }} justifyContent="center">
          <Box md={{ column: 9 }}>
            <Box padding={2} marginLeft={2}>
              <Text align="center" color="darkGray" size="md" weight="bold">
                {i18n._(
                  'If you’ve found content that you believe violates your intellectual property, ' +
                    'you can fill out our copyright complaint form.',
                  'explanation of IP complaint process',
                )}
              </Text>
            </Box>
            <Box padding={2}>
              <Text align="center" smSize="xs" mdSize="sm" lgSize="md">
                <NavigateLink
                  to={urlUtil.appendQueryString(ABOUT_DMCA_PIN_URL, {
                    pin_id: pinId,
                    user_did_it_id: didItDataId,
                  })}
                  className={cx(['Button', 'btn', 'primary', 'rounded'])}
                  target
                >
                  {i18n._('File a report', 'link to DMCA form')}
                </NavigateLink>
              </Text>
            </Box>
          </Box>
        </Box>
      );
    } else {
      return null;
    }
  };

  render() {
    const { onClose, users } = this.props;
    const { flagReason, flow, status, selectedReason } = this.state;

    const submitButton = (
      <LegacyButton
        className="reportButton"
        htmlType="submit"
        id="submitButton"
        loading={status === Status.PROCESSING}
        onClick={this.handleSubmit}
        text={i18n._('Report content', 'button to report content')}
        type="primary"
      />
    );

    return (
      <Flag
        className={cx({
          FlagContent: flow === Flow.REPORT,
        })}
        content={this.renderContent()}
        flagType={FlagType.DID_IT}
        flow={flow}
        onFlowStatusChange={this.handleFlowStatusChange}
        onClose={onClose}
        onNext={this.handleNext}
        onRevert={this.handleRevert}
        onSelectReason={this.handleSelectReason}
        showPrimaryAction={flagReason !== IP_CASE}
        submitButton={submitButton}
        status={status}
        selectedReason={selectedReason || ''}
        flagReason={flagReason || ''}
        users={users}
        title={this.getTitle()}
        flagOptionsConfig={{
          __default: [
            {
              text: i18n._("It isn't useful or relevant", 'tried-it reporting flow'),
              value: 'ignoreThisPin',
              isTerminalReason: true,
              category: 'ignoreThisPin',
            },
            {
              text: i18n._("It shouldn't be on Pinterest", 'tried-it reporting flow'),
              value: POLICIES_CASE,
              isTerminalReason: false,
            },
            {
              text: i18n._("It's spam", 'tried-it reporting flow'),
              value: 'spam',
              isTerminalReason: true,
              category: 'spam',
            },
            {
              text: i18n._("It's my intellectual property", 'tried-it reporting flow'),
              isTerminalReason: false,
              value: 'IP',
            },
          ],
          ignoreThisPin: [],
          policies: [
            {
              text: i18n._("It's sexually explicit", 'tried-it reporting flow'),
              value: 'nudity',
              isTerminalReason: true,
              category: 'nudity',
            },
            {
              text: i18n._("It's self-harm", 'an option on tried-it reporting flow'),
              value: 'self-harm',
              isTerminalReason: true,
              category: 'self-harm',
            },
            {
              text: i18n._("It's hate speech", 'tried-it reporting flow'),
              value: 'hate-speech',
              isTerminalReason: true,
              category: 'hate-speech',
            },
            {
              text: i18n._("It's harassment or bullying", 'tried-it reporting flow'),
              value: 'attacks',
              isTerminalReason: false,
              category: 'attacks',
            },
            {
              text: i18n._("It's graphic violence", 'tried-it reporting flow'),
              value: 'gore',
              isTerminalReason: true,
              category: 'graphic-violence',
            },
          ],
          attacks: [
            {
              text: i18n._("It's a photo of me or my child", 'tried-it reporting flow'),
              value: 'harassingMeOrMyChild',
              isTerminalReason: true,
              category: 'attacks',
            },
            {
              text: i18n._(
                'It identifies and attacks me or someone I know',
                'tried-it reporting flow',
              ),
              value: 'harassingMeOrAFriend',
              isTerminalReason: true,
              category: 'attacks',
            },
            {
              text: i18n._('Something else', 'tried-it reporting flow'),
              value: 'harassingOther',
              isTerminalReason: true,
              category: 'attacks',
            },
          ],
          IP: [],
          spam: [],
        }}
      />
    );
  }
}

function mapDispatchToProps(dispatch: Dispatch<any>): ReduxDispatchProps {
  return {
    reportDidIt: (
      aggregatedPinDataId: string,
      detailedReasons: ?Array<?string>,
      id: string,
      reason: ?string,
      username: string,
    ) => dispatch(reportUserDidItData(aggregatedPinDataId, detailedReasons, id, reason, username)),
  };
}

export default connect<Props, OwnProps, _, _, _, _>(
  null,
  mapDispatchToProps,
)(FlagDidItActivity);
